import { EntityId } from '@reduxjs/toolkit';

export type EntityCompareFunc<T> = ([, data]: [string, T | undefined]) => boolean;

/**
 * A curried function that accepts a compare function
 * and filters entities accordingly. This can be used inside
 * selectFromResult in useWhateverQuery.
 *
 * Example:
 *
 * const query = useWhateverQuery(undefined, {
 *    selectFromResult: (result) => {
 *      const { data, ...rest } = result;
 *
 *      const compare = ([, data]) => data.hasAttribute
 *      const filterFunc = entityFilter<Foo>(compare);
 *      const { ids, entities } = filterFunc(data?.entities);
 *
 *      return { ...rest, data: { ids, entities } };
 *    },
 * });
 *
 * @export
 * @template T
 * @param {EntityCompareFunc<T>} compareFunc
 * @return {*}
 */
export function entityFilter<T = unknown>(compareFunc: EntityCompareFunc<T>) {
  return function (entities: Record<EntityId, T> | undefined) {
    const filteredEntities = Object.fromEntries(Object.entries(entities || {}).filter(compareFunc));
    const filteredIds = Object.keys(filteredEntities);

    return { entities: filteredEntities, ids: filteredIds };
  };
}
